import logo from "assets/logo_verify.svg";
import { Box, Typography, Button } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useResetPasswordMutation } from "../../redux/api/authApi";
import FormInputPassword from "../../components/FormInputPassword";

const resetPasswordSchema = object({
  password: string()
    .min(1, "La contraseña es obligatoria")
    .min(8, "La contraseña debe tener al menos 8 caracteres"),
  passwordConfirm: string().min(1, "Introduce la nueva contraseña"),
}).refine((data) => data.password === data.passwordConfirm, {
  message: "Las contraseñas no coinciden",
  path: ["passwordConfirm"],
});

export type ResetPasswordInput = TypeOf<typeof resetPasswordSchema>;

const ResetPasswordPage = () => {
  const { resetToken } = useParams<{ resetToken: string }>();

  const methods = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  // 👇 API Login Mutation
  const [resetPassword, { isError, error, isSuccess }] =
    useResetPasswordMutation();

  const navigate = useNavigate();

  const {
    handleSubmit,
    setError,
  } = methods;
  
  const navigateToLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    if (isError && error) {
        setError("passwordConfirm", { type: "manual", message: "No se ha podido actualizar la contraseña" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const onSubmitHandler: SubmitHandler<ResetPasswordInput> = (values) => {
    resetPassword({ ...values, resetToken: resetToken! });
  };
  
  if (isSuccess) {
    return (
      <Box
      sx={{
        width: "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "3rem",
        "@media (min-width:900px)": {
          width: "80%", // Para md
        },
        "@media (min-width:1200px)": {
          width: "60%",
        },
        "@media (min-width:1536px)": {
          width: "60%", // Para xl
        },
      }}
    >
         <Box
           sx={{
            width: "40%",
            marginBottom: "3rem",
            "@media (min-width:900px)": {
              width: "90%", // Para md
            },
            "@media (min-width:1200px)": {
              width: "80%",
            },
            "@media (min-width:1536px)": {
              width: "70%", // Para xl
            },
          }}
          >
            <img src={logo} className="img-responsive" alt="Logo" />
          </Box>
          <Box sx={{ width: "100%" }}>
          <Typography
             component="h1"
             color="primary"
             sx={{
               fontStyle: "normal",
               fontWeight: 600,
               lineHeight: "1.8125rem",
               textAlign: "center",
               width: "100%",
               mb: ".3rem",
               fontSize: { xs: "1.4rem", xl: "1.4rem" },
             }}
          >
            Restablecer contraseña
          </Typography>
          <Typography
             variant="body1"
             component="h2"
             sx={{
               color: "#A7ABBB",
               mb: 4,
               textAlign: "center",
               fontSize: { xs: ".8rem", xl: ".8rem" },
               fontStyle: "normal",
               fontWeight: 500,
               lineHeight: "0.9375rem" /* 93.75% */,
             }}
          >
           Se ha restablecido tu contraseña correctamente. Ahora puedes iniciar sesión con tu nueva contraseña
          </Typography>
          <Button sx={{ mt: 3, width: "100%" , fontSize: "1.1rem"}} onClick={navigateToLogin}>
            Volver a inicio de sesión
          </Button>
      </Box>
      </Box>        );
  }

  return (
    <Box
    sx={{
      width: "80%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "3rem",
      "@media (min-width:900px)": {
        width: "80%", // Para md
      },
      "@media (min-width:1200px)": {
        width: "60%",
      },
      "@media (min-width:1536px)": {
        width: "60%", // Para xl
      },
    }}
  >
        <Box
            sx={{
              width: "40%",
              marginBottom: "3rem",
              "@media (min-width:900px)": {
                width: "90%", // Para md
              },
              "@media (min-width:1200px)": {
                width: "80%",
              },
              "@media (min-width:1536px)": {
                width: "70%", // Para xl
              },
            }}
          >
          <img src={logo} className="img-responsive" alt="Logo" />
        </Box>
        <Box sx={{ width: "100%" }}>
        <Typography
              component="h1"
              color="primary"
              sx={{
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "1.8125rem",
                width: "100%",
                mb: ".3rem",
                fontSize: { xs: "1.4rem", xl: "1.4rem" },
              }}
            >
         Restablecer contraseña
        </Typography>
        <Typography
              variant="body1"
              component="h2"
              sx={{
                color: "#A7ABBB",
                mb: 4,
                fontSize: { xs: ".8rem", xl: ".8rem" },
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "0.9375rem" /* 93.75% */,
              }}
            >
          Escribe tu nueva contraseña y repitela para confirmarla
        </Typography>
        <FormProvider {...methods}>
         <Box
              component="form"
              onSubmit={handleSubmit(onSubmitHandler)}
              noValidate
              autoComplete="off"
              width="100%"
            >
            <FormInputPassword
              name="password"
              label="Nueva contraseña"
              sx={{
                mb: 2,
                ".MuiTypography-body2": {
                  fontWeight: 600,
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  lineHeight: "1.8125rem",
                },
                ".MuiInput-root": {
                  fontSize: ".9rem",
                  padding: 0,
                  height: "32px",
                  lineHeight: "1.8125rem",
                  color: "#718096",
                },
              }}
            />
            <FormInputPassword
              name="passwordConfirm"
              label="Repite la contraseña"
              
              sx={{
                mb: 2,
                ".MuiTypography-body2": {
                  fontWeight: 600,
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  lineHeight: "1.8125rem",
                },
                ".MuiInput-root": {
                  fontSize: ".9rem",
                  padding: 0,
                  height: "32px",
                  lineHeight: "1.8125rem",
                  color: "#718096",
                },
              }}
            />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "2rem",
              }}
            >
              <Button
                variant="contained"
                sx={{ mt: 3, width: "100%" , fontSize: "1.1rem"}}
                disableElevation
                type="submit"
              >
                Actualizar contraseña
              </Button>
            </Box>
          </Box>
        </FormProvider>
        </Box>
     </Box>   
  );
};

export default ResetPasswordPage;
